import type { WheelLanding, WheelSegment } from '@goodtime/shared';
import type { GameView } from './core';

export type WheelView = GameView<WheelLanding, WheelLanding>;

/** A segment's label on the wheel. */
export function segmentLabel(s: WheelSegment): string {
  if (s.kind === 'fs') return `${s.freeSpins} FREE`;
  if (s.goldPot) return 'GOLD POT';
  return s.coins.toLocaleString('en-GB');
}

/**
 * The wheel's final rotation (radians, clockwise) to stop segment `index`
 * under the pointer at the top, after `turns` full turns, from `from`.
 * Segment 0 starts centred at the top.
 */
export function wheelStop(index: number, count: number, from: number, turns = 5): number {
  const step = (Math.PI * 2) / count;
  const target = -index * step;
  const base = from - (from % (Math.PI * 2));
  return base + turns * Math.PI * 2 + (((target % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2));
}

/** Whether the wheel has the Gold Pot on it (the server deals it, per
 *  play, only when this play can win the pot). */
export const hasGoldPot = (segments: readonly WheelSegment[]): boolean => segments.some((s) => s.goldPot);

/** The line under the wheel's title. With the Gold Pot on it, two short
 *  sentences: a phone shows them on two lines, never a word or two alone
 *  on a third. */
export function wheelLead(view: WheelView, goldPot = false): string {
  if (view.phase === 'over') return view.prize?.gold ? 'The Gold Pot!' : 'Round and round, and there it stops.';
  return goldPot ? 'Every segment pays. One wins the Gold Pot.' : 'Spin the wheel. Every segment pays.';
}

/** What the wheel does, said on a tap. */
export function wheelExplain(segments: readonly WheelSegment[]): string {
  const parts = ['SPIN, and you win whatever stops under the pointer.'];
  if (segments.some((s) => s.kind === 'fs')) parts.push('FREE means free spins on this slot.');
  if (hasGoldPot(segments)) parts.push('GOLD POT wins the whole Gold Pot.');
  return parts.join(' ');
}
